// src/hooks/useMasterList.js
import { useState, useEffect, useCallback, useRef } from 'react';
import masterApiCall from 'shared/services/MasterDataApi';

const CACHE_KEY = 'masterDataCache';
const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds

/**
 * A custom hook to fetch master data (curricula, academic classes, and subjects).
 * It handles the loading state, error state, and data transformation for dropdowns.
 * This version includes client-side caching with options to enable/disable/clear the cache.
 *
 * @param {object} options
 * @param {boolean} options.useMockData - Flag to determine if mock data should be used.
 * @param {function} options.displayToast - Function to display a toast notification.
 * @param {function} options.apiCall - The API adapter function to perform HTTP requests.
 * @returns {{
 * masterData: object,
 * isLoading: boolean,
 * error: string|null,
 * enableCache: function,
 * disableCache: function,
 * clearCache: function
 * }}
 */
export const useMasterList = ({ useMockData, displayToast, startLoading, stopLoading, apiCall = masterApiCall }) => {
    // State to hold the fetched master data, transformed for dropdowns
    const [masterData, setMasterData] = useState({
        curricula: [],
        academicClasses: [],
        subjects: [],
        chapters: [],
        questionTypes: [],
        tags: [],
    });
    // State to track the loading status of the master data fetch
    const [isLoading, setIsLoading] = useState(true);
    // State to hold any error messages
    const [error, setError] = useState(null);
    // State to control caching behavior, defaults to enabled
    const [isCacheEnabled, setIsCacheEnabled] = useState(true);

    // Stable refs to avoid effect loops when parent passes new function identities
    const displayToastRef = useRef(displayToast);
    const startLoadingRef = useRef(startLoading);
    const stopLoadingRef = useRef(stopLoading);

    useEffect(() => { displayToastRef.current = displayToast; }, [displayToast]);
    useEffect(() => { startLoadingRef.current = startLoading; }, [startLoading]);
    useEffect(() => { stopLoadingRef.current = stopLoading; }, [stopLoading]);

    // Function to clear the master data cache from sessionStorage
    const clearCache = useCallback((showToast = false) => {
        sessionStorage.removeItem(CACHE_KEY);
        console.log('[useMasterList] Master data cache cleared.');
        if (showToast && displayToastRef.current) {
            displayToastRef.current('Master data cache cleared.', 'info');
        }
    }, []);

    // Function to enable caching
    const enableCache = useCallback(() => {
        setIsCacheEnabled(true);
        console.log('[useMasterList] Caching enabled.');
    }, []);

    // Function to disable caching
    const disableCache = useCallback(() => {
        setIsCacheEnabled(false);
        clearCache(); // Clear the cache when disabling
        console.log('[useMasterList] Caching disabled and cleared.');
    }, [clearCache]);

    // Use useCallback to memoize the fetch function
    const fetchMasterData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        if (startLoadingRef.current) startLoadingRef.current();

        try {
            // Check cache first if enabled
            if (isCacheEnabled) {
                const cachedData = sessionStorage.getItem(CACHE_KEY);
                if (cachedData) {
                    const { timestamp, data } = JSON.parse(cachedData);
                    const now = new Date().getTime();
                    const cacheValid = now - timestamp < CACHE_DURATION;
                    const hasAllKeys = data && Array.isArray(data.curricula) && Array.isArray(data.academicClasses) && Array.isArray(data.subjects) && Array.isArray(data.chapters) && Array.isArray(data.rawChapters) && Array.isArray(data.questionTypes) && Array.isArray(data.tags);
                    if (cacheValid && hasAllKeys) {
                        console.log('[useMasterList] Cache hit. Returning cached data.');
                        setMasterData(data);
                        setIsLoading(false);
                        if (stopLoadingRef.current) stopLoadingRef.current();
                        return; // Exit early
                    } else {
                        if (cacheValid && !hasAllKeys) {
                            console.log('[useMasterList] Cache missing keys. Invalidating and refetching.');
                        }
                        console.log('[useMasterList] Cache expired. Fetching new data.');
                        clearCache(); // Clear expired cache
                    }
                }
            }

            // Fetch data from API if cache is disabled or expired
            const [curriculaData, academicClassesData, subjectsData, chaptersData, tagsData] = await Promise.all([
                apiCall("/curricula", useMockData, 'GET', null, null, null, displayToastRef.current),
                apiCall("/class-standards", useMockData, 'GET', null, null, null, displayToastRef.current),
                apiCall("/subjects", useMockData, 'GET', null, null, null, displayToastRef.current),
                apiCall("/chapters", useMockData, 'GET', null, null, null, displayToastRef.current),
                apiCall("/tags", useMockData, 'GET', null, null, null, displayToastRef.current),
            ]);

            // Transform the data to {value, label} format
            const transformedData = {
                curricula: (curriculaData || []).map(item => ({
                    id: item.id,
                    value: item.code,
                    label: item.name,
                })),
                academicClasses: (academicClassesData || []).map(item => ({
                    id: item.id,
                    value: item.code,
                    label: item.name,
                })),
                subjects: (subjectsData || []).map(item => ({
                    id: item.id,
                    value: item.code,
                    label: item.name,
                })),
                chapters: (chaptersData || []).map(item => ({
                    id: item.id,
                    value: item.code,
                    label: item.name,
                    // Include the subject's value (code) for filtering
                    subjectValue: (subjectsData || []).find(subject => subject.id === item.subject?.id)?.code,
                    // Include the class standard's value (code) for filtering
                    classStandardValue: (academicClassesData || []).find(ac => ac.id === item.classStandard?.id || ac.id === item.academicClass?.id)?.code,
                })),
                rawChapters: chaptersData || [],
                // Static question types for now; later fetch from API
                questionTypes: [
                    { value: 'MULTIPLE_CHOICE', label: 'Multiple Choice' },
                    { value: 'TRUE_FALSE', label: 'True/False' },
                    { value: 'FILL_IN_THE_BLANK', label: 'Fill in the Blank' },
                    { value: 'MATCHING', label: 'Matching' },
                    { value: 'OPEN_ENDED', label: 'Open Ended' },
                    { value: 'QUESTION_TABLE', label: 'Question Table' },
                    { value: 'MATHS_CALCULATION', label: 'Maths Calculation' },
                ],
                tags: (tagsData || []).map(item => ({
                    id: item.id,
                    value: item.id,
                    label: item.name,
                })),
            };

            // Store the new data in cache if enabled
            if (isCacheEnabled) {
                const dataToCache = {
                    timestamp: new Date().getTime(),
                    data: transformedData,
                };
                sessionStorage.setItem(CACHE_KEY, JSON.stringify(dataToCache));
                console.log('[useMasterList] New data fetched and cached.');
            }

            setMasterData(transformedData);

        } catch (err) {
            console.error("[useMasterList] Failed to fetch master data:", err);
            setError("Failed to load form options.");
            if (displayToastRef.current) {
                displayToastRef.current(`Failed to load form options: ${err.message}`, 'error');
            }
        } finally {
            setIsLoading(false);
            if (stopLoadingRef.current) stopLoadingRef.current();
        }
    }, [isCacheEnabled, useMockData, apiCall, clearCache]);

    // Fetch the data when the hook is first used or when caching state changes
    useEffect(() => {
        fetchMasterData();
    }, [fetchMasterData]);

    return { masterData, isLoading, error, enableCache, disableCache, clearCache };
};